/**
 * Contrato único de funnel de attribution → PostHog + DB + reportes admin.
 * Importar desde frontend (@shared) y backend (../../shared/).
 */

export type AttributionEventName =
  | "session_started"
  | "page_viewed"
  | "hero_cta_clicked"
  | "checkout_opened"
  | "checkout_almost_purchased"
  | "checkout_abandoned"
  | "conversion"
  | "vip_video_play"
  | "form_submitted";

export const FUNNEL_STAGE: Record<AttributionEventName, string> = {
  session_started: "1_session",
  page_viewed: "2_page",
  hero_cta_clicked: "3_hero_cta",
  vip_video_play: "3_video",
  checkout_opened: "4_checkout_open",
  checkout_almost_purchased: "4b_almost_purchased",
  checkout_abandoned: "4c_abandoned",
  form_submitted: "4_form",
  conversion: "5_purchased",
};

export const ATTRIBUTION_EVENT_NAMES: readonly AttributionEventName[] = [
  "session_started",
  "page_viewed",
  "hero_cta_clicked",
  "vip_video_play",
  "checkout_opened",
  "checkout_almost_purchased",
  "checkout_abandoned",
  "form_submitted",
  "conversion",
] as const;

export const ATTRIBUTION_EVENT_NAME_SET: ReadonlySet<AttributionEventName> =
  new Set(ATTRIBUTION_EVENT_NAMES);

/** Eventos del embudo principal en orden (reportes + HogQL). */
export const GROWTH_FUNNEL_EVENTS: readonly AttributionEventName[] = [
  "session_started",
  "page_viewed",
  "hero_cta_clicked",
  "checkout_opened",
  "checkout_almost_purchased",
  "checkout_abandoned",
  "conversion",
] as const;

/** Eventos server-side que enriquecen con IP / iplocate. */
export const IPL_ENRICH_EVENTS: ReadonlySet<AttributionEventName> = new Set([
  "checkout_almost_purchased",
  "checkout_abandoned",
  "conversion",
]);

export const ATTRIBUTION_EVENT_LABELS: Record<AttributionEventName, string> = {
  session_started: "Sesión iniciada",
  page_viewed: "Page view",
  hero_cta_clicked: "Hero CTA click",
  checkout_opened: "Checkout abierto",
  checkout_almost_purchased: "Casi compró (email)",
  checkout_abandoned: "Checkout abandonado",
  conversion: "Conversión",
  vip_video_play: "VIP video play",
  form_submitted: "Form enviado",
};

export function attributionEventLabel(name: string): string {
  return (
    ATTRIBUTION_EVENT_LABELS[name as AttributionEventName] ?? name
  );
}

/** Claves canónicas PostHog en propiedades de evento. */
export const PH_SESSION_PROP = "ph_session_id" as const;
export const PH_DISTINCT_PROP = "ph_distinct_id" as const;

/** Alias en Stripe metadata y bodies API (mantener ambos al escribir). */
export const STRIPE_PH_SESSION_KEYS = [
  "ph_session_id",
  "posthog_session_id",
  "session_id",
] as const;

export const STRIPE_PH_DISTINCT_KEYS = [
  "ph_distinct_id",
  "posthog_distinct_id",
  "distinct_id",
] as const;

function firstNonEmptyString(
  sources: Record<string, unknown> | null | undefined,
  keys: readonly string[],
): string | null {
  if (!sources) return null;
  for (const key of keys) {
    const v = sources[key];
    if (typeof v === "string") {
      const t = v.trim();
      if (t.length > 0) return t;
    }
  }
  return null;
}

export function resolvePhSessionId(
  sources: Record<string, unknown> | null | undefined,
): string | null {
  return firstNonEmptyString(sources, STRIPE_PH_SESSION_KEYS);
}

export function resolvePhDistinctId(
  sources: Record<string, unknown> | null | undefined,
): string | null {
  return firstNonEmptyString(sources, STRIPE_PH_DISTINCT_KEYS);
}

/** Escribe ambos alias en metadata Stripe para lectura uniforme en webhooks. */
export function buildStripePosthogMetadata(opts: {
  sessionId?: string | null;
  distinctId?: string | null;
}): Record<string, string> {
  const out: Record<string, string> = {};
  const sid = opts.sessionId?.trim();
  const did = opts.distinctId?.trim();
  if (sid) {
    out.ph_session_id = sid.slice(0, 120);
    out.posthog_session_id = sid.slice(0, 120);
  }
  if (did) {
    out.ph_distinct_id = did.slice(0, 120);
    out.posthog_distinct_id = did.slice(0, 120);
  }
  return out;
}

/** Kind en dual_cta_funnel_events — alineado con attribution event names. */
export const DUAL_CTA_FUNNEL_KIND = {
  almostPurchased: "checkout_almost_purchased",
  abandoned: "checkout_abandoned",
} as const;

/** Fuentes que emiten checkout_almost_purchased (no webhook_expired). */
export const CHECKOUT_ALMOST_SOURCES = new Set([
  "api",
  "client",
  "cron",
  "webhook_completed_unpaid",
]);

export type StripeCheckoutEmailCaptureSource =
  | "webhook_expired"
  | "webhook_completed_unpaid"
  | "cron"
  | "api"
  | "client";

export function shouldEmitCheckoutAlmostPurchased(
  source: StripeCheckoutEmailCaptureSource | undefined,
): boolean {
  return source != null && CHECKOUT_ALMOST_SOURCES.has(source);
}